import { homeRoute } from './../../core/routing/route-names';
import { OnInit, Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { ChangePasswordDTO, UserAccountClient } from '../models/models.swagger.generated';
import { ToastService } from '../services/toast.service';
import { passwordValidation } from '../directives/password-validation.directive';
import { TranslatePipe } from '@ngx-translate/core';

import { AppErrorMessagesComponent } from '../error-messages/app-error-messages.component';
import { PasswordInputComponent } from '../shared/password-input/password-input.component';

export type ChangePasswordFormType = FormGroup<{
    oldPassword: FormControl<string | null>;
    newPassword: FormControl<string | null>;
    newPasswordConfirm: FormControl<string | null>;
  }>;

@Component({
    selector: 'app-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ReactiveFormsModule, PasswordInputComponent, AppErrorMessagesComponent, TranslatePipe]
})
export class ChangePasswordComponent {
  public loading = false;
  public changePasswordForm!: ChangePasswordFormType;
  public oldPasswordControl!: FormControl<string | null>;
  public newPasswordControl!: FormControl<string | null>;
  public newPasswordConfirmControl!: FormControl<string | null>;
  private changePasswordDTO: ChangePasswordDTO | null = null;

  constructor(
    private router: Router,
    private userAccountClient: UserAccountClient,
    private toastService: ToastService) {
      this.initializeForm();
  }

  hasValidPasswords(group: ChangePasswordFormType | null): boolean {
    const pass = group?.value.newPassword;
    const confirmPass = group?.value.newPasswordConfirm;

    if (!pass || !confirmPass || pass !== confirmPass) {
      this.loading = false;
      this.toastService.showError(
        'i18n.core.messages.changePassword.title',
        'i18n.core.messages.changePassword.pwdDoNotMatch');
      return false;
    }

    return true;
  }

  public changePassword(): void {
    this.loading = true;

    this.changePasswordDTO = new ChangePasswordDTO();
    this.changePasswordDTO.oldPassword = this.changePasswordForm?.value.oldPassword || '';
    this.changePasswordDTO.newPassword = this.changePasswordForm?.value.newPassword || '';

    if (!this.hasValidPasswords(this.changePasswordForm)) {
      return;
    }

    this.userAccountClient.changePassword(this.changePasswordDTO).subscribe({
      next: (result) => {
        this.loading = false;

        if (result) {
          this.router.navigate([homeRoute]);
        }
      },
      error: error => {
        this.loading = false;

        this.toastService.showApiException(error,
          'i18n.core.messages.changePassword.title',
          'i18n.core.messages.changePassword.message');
      }}
    );
  }

  private initializeForm(): void {
    this.oldPasswordControl = new FormControl<string>('', [Validators.required]);
    this.newPasswordControl = new FormControl<string>('', [Validators.required, passwordValidation()]);
    this.newPasswordConfirmControl = new FormControl<string>('', [Validators.required, passwordValidation()]);
    this.changePasswordForm = new FormGroup({
      oldPassword: this.oldPasswordControl,
      newPassword: this.newPasswordControl,
      newPasswordConfirm: this.newPasswordConfirmControl
    });
  }
}
